import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { LibraryEditor } from "./use-library-editor"

import { FolderOpen, Plus, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { CsvInput, EditorSectionCard, FieldRow, SwitchRow } from "./editor-fields"
import {
  isDocumentLibraryType,
  isListType,
  isPagesType,
  isSharePointFamily,
} from "./library-editor-model"

type ContentSectionProps = {
  editor: LibraryEditor
}

function str(value: unknown): string {
  return typeof value === "string" ? value : ""
}

function strList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : []
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

export function ContentSection({ editor }: ContentSectionProps) {
  const { t } = useTranslation()
  const { values } = editor
  const config = values.source_config
  const sourceType = values.source_type
  const listItem = record(config.list_item)
  const accessControl = record(record(config.pages).access_control)

  return (
    <EditorSectionCard
      icon={<FolderOpen className="size-4" />}
      id="library-content"
      open={editor.openSections.content}
      title={t("adminDashboard.libraries.editor.sections.content", "Content")}
      onOpenChange={(open) => editor.setSectionOpen("content", open)}
    >
      {isSharePointFamily(sourceType) && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FieldRow
              id="language"
              label={t("adminDashboard.libraries.editor.language", "Language")}
              help={t(
                "adminDashboard.libraries.editor.languageHelp",
                "Language code for document processing."
              )}
            >
              <Input
                id="language"
                value={str(config.language)}
                placeholder={t(
                  "adminDashboard.libraries.editor.languagePlaceholder",
                  "e.g., en, sv, de"
                )}
                onChange={(event) =>
                  editor.updateSourceConfig("language", event.target.value || undefined)
                }
              />
            </FieldRow>
            <FieldRow
              id="content_type"
              label={t("adminDashboard.libraries.editor.contentType", "Content type")}
              help={t(
                "adminDashboard.libraries.editor.contentTypeHelp",
                "SharePoint content type filter for indexing."
              )}
            >
              <Input
                id="content_type"
                value={str(config.content_type)}
                placeholder={t(
                  "adminDashboard.libraries.editor.contentTypePlaceholder",
                  "e.g., Document, Page"
                )}
                onChange={(event) =>
                  editor.updateSourceConfig("content_type", event.target.value || undefined)
                }
              />
            </FieldRow>
          </div>
          <FieldRow
            id="exclude_folders"
            label={t("adminDashboard.libraries.editor.excludeFolders", "Exclude folders")}
            help={t(
              "adminDashboard.libraries.editor.excludeFoldersHelp",
              "Comma-separated folder names to skip during indexing."
            )}
          >
            <CsvInput
              id="exclude_folders"
              values={strList(config.exclude_folders)}
              placeholder={t(
                "adminDashboard.libraries.editor.excludeFoldersPlaceholder",
                "e.g., Archive, Old Documents"
              )}
              onChange={(folders) => editor.updateSourceConfig("exclude_folders", folders)}
            />
          </FieldRow>
          {isDocumentLibraryType(sourceType) && (
            <>
              <FieldRow
                id="folder_content_type"
                help={t(
                  "adminDashboard.libraries.editor.folderContentTypeHelp",
                  "Content type that marks folders, when the site uses a custom one."
                )}
                label={t(
                  "adminDashboard.libraries.editor.folderContentType",
                  "Folder content type"
                )}
              >
                <Input
                  id="folder_content_type"
                  placeholder="Folder"
                  value={str(config.folder_content_type)}
                  onChange={(event) =>
                    editor.updateSourceConfig(
                      "folder_content_type",
                      event.target.value || undefined
                    )
                  }
                />
              </FieldRow>
              <SwitchRow
                checked={config.replicate_permissions === true}
                id="replicate_permissions"
                help={t(
                  "adminDashboard.libraries.editor.replicatePermissionsHelp",
                  "Read each document's real SharePoint permissions and use them as that document's access list. Documents without resolvable permissions fall back to the library policy. Requires permission-read access for the app registration."
                )}
                label={t(
                  "adminDashboard.libraries.editor.replicatePermissions",
                  "Replicate SharePoint document permissions"
                )}
                onCheckedChange={(checked) =>
                  editor.updateSourceConfig("replicate_permissions", checked)
                }
              />
            </>
          )}
          {isListType(sourceType) && (
            <div className="space-y-3 rounded-lg border p-3">
              <p className="text-sm font-medium">
                {t("adminDashboard.libraries.editor.listSettings", "List settings")}
              </p>
              <SwitchRow
                checked={listItem.fetch_attachments !== false}
                id="fetch_attachments"
                label={t("adminDashboard.libraries.editor.fetchAttachments", "Fetch attachments")}
                help={t(
                  "adminDashboard.libraries.editor.fetchAttachmentsHelp",
                  "Download and index each item's attachments."
                )}
                onCheckedChange={(checked) =>
                  editor.updateNestedSourceConfig(["list_item"], "fetch_attachments", checked)
                }
              />
              <SwitchRow
                checked={listItem.all_items_to_one_file === true}
                id="all_items_to_one_file"
                help={t(
                  "adminDashboard.libraries.editor.allItemsToOneFileHelp",
                  "Combine every list item into a single document instead of one per item."
                )}
                label={t(
                  "adminDashboard.libraries.editor.allItemsToOneFile",
                  "All items in one document"
                )}
                onCheckedChange={(checked) =>
                  editor.updateNestedSourceConfig(["list_item"], "all_items_to_one_file", checked)
                }
              />
              <SwitchRow
                checked={listItem.compact_combined_format === true}
                id="compact_combined_format"
                help={t(
                  "adminDashboard.libraries.editor.compactCombinedFormatHelp",
                  "Render combined items compactly, keeping only the metadata fields below."
                )}
                label={t(
                  "adminDashboard.libraries.editor.compactCombinedFormat",
                  "Compact combined format"
                )}
                onCheckedChange={(checked) =>
                  editor.updateNestedSourceConfig(["list_item"], "compact_combined_format", checked)
                }
              />
              {listItem.compact_combined_format === true && (
                <FieldRow
                  id="compact_metadata_fields"
                  help={t(
                    "adminDashboard.libraries.editor.compactMetadataFieldsHelp",
                    "Comma-separated column names kept in the compact format."
                  )}
                  label={t(
                    "adminDashboard.libraries.editor.compactMetadataFields",
                    "Compact metadata fields"
                  )}
                >
                  <CsvInput
                    id="compact_metadata_fields"
                    placeholder="Title, Modified, Status"
                    values={strList(listItem.compact_metadata_fields)}
                    onChange={(fields) =>
                      editor.updateNestedSourceConfig(
                        ["list_item"],
                        "compact_metadata_fields",
                        fields
                      )
                    }
                  />
                </FieldRow>
              )}
            </div>
          )}
          {isPagesType(sourceType) && (
            <div className="space-y-3 rounded-lg border p-3">
              <p className="text-sm font-medium">
                {t("adminDashboard.libraries.editor.pageAccess", "Page access control")}
              </p>
              <p className="text-muted-foreground text-xs">
                {t(
                  "adminDashboard.libraries.editor.pageAccessHelp",
                  "Paths into the page metadata that hold the user ids and group names allowed to see each page. Leave empty to use the library policy."
                )}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FieldRow
                  id="oids_path"
                  label={t("adminDashboard.libraries.editor.oidsPath", "User ids path")}
                >
                  <Input
                    id="oids_path"
                    placeholder="fields/AllowedUsers"
                    value={str(accessControl.oids_path)}
                    onChange={(event) =>
                      editor.updateNestedSourceConfig(
                        ["pages", "access_control"],
                        "oids_path",
                        event.target.value || undefined
                      )
                    }
                  />
                </FieldRow>
                <FieldRow
                  id="groups_path"
                  label={t("adminDashboard.libraries.editor.groupsPath", "Groups path")}
                >
                  <Input
                    id="groups_path"
                    placeholder="fields/AllowedGroups"
                    value={str(accessControl.groups_path)}
                    onChange={(event) =>
                      editor.updateNestedSourceConfig(
                        ["pages", "access_control"],
                        "groups_path",
                        event.target.value || undefined
                      )
                    }
                  />
                </FieldRow>
              </div>
            </div>
          )}
        </>
      )}

      <ExcludePatterns editor={editor} />
    </EditorSectionCard>
  )
}

/** Glob patterns skipped during indexing (top-level `exclude_patterns`). */
function ExcludePatterns({ editor }: { editor: LibraryEditor }) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState("")
  const patterns = editor.values.exclude_patterns

  const add = () => {
    const pattern = draft.trim()
    if (!pattern || patterns.includes(pattern)) return
    editor.update({ exclude_patterns: [...patterns, pattern] })
    setDraft("")
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="exclude_pattern">
        {t("adminDashboard.libraries.editor.excludePatterns", "Exclusion patterns")}
      </Label>
      <p className="text-muted-foreground text-xs">
        {t(
          "adminDashboard.libraries.editor.excludePatternsHelp",
          "Files matching these glob patterns are skipped during indexing. Examples: *.tmp, ~$*, **/cache/*"
        )}
      </p>
      <div className="flex gap-2">
        <Input
          id="exclude_pattern"
          value={draft}
          placeholder={t(
            "adminDashboard.libraries.editor.excludePatternPlaceholder",
            "e.g., *.tmp or ~$*"
          )}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              add()
            }
          }}
        />
        <Button disabled={!draft.trim()} size="icon" type="button" variant="outline" onClick={add}>
          <Plus className="size-4" />
        </Button>
      </div>
      {patterns.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {patterns.map((pattern) => (
            <Badge key={pattern} className="gap-1 pr-1" variant="secondary">
              <code className="text-xs">{pattern}</code>
              <Button
                className="hover:bg-destructive hover:text-destructive-foreground -m-1 size-6 p-0"
                size="icon"
                type="button"
                variant="ghost"
                onClick={() =>
                  editor.update({ exclude_patterns: patterns.filter((p) => p !== pattern) })
                }
              >
                <X className="size-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
